import { Button, CircularProgress, DialogActions } from '@mui/material';
import { useId } from 'react';
import { sortBy } from 'remeda';
import { getActionButtonSx, getModalFooterSx } from './modal-container.styles';
import type { ModalAction, ModalActions } from './modal-container.types';
import { isSubmitAction, MODAL_ACTION_ATTRIBUTE } from './modal-container.utils';

const LOADER_SIZE = 12;

interface ModalActionButtonProps {
  action: ModalAction;
  hasDanger: boolean;
  onDismiss: () => void;
  isSaving: boolean;
}

const ModalActionButton = ({
  action,
  hasDanger,
  onDismiss,
  isSaving,
}: ModalActionButtonProps) => {
  const { label, kind, onClick, icon, disabled, loading, loadingLabel, testId } = action;
  const isSubmit = isSubmitAction(action);
  const buttonId = useId();

  return (
    <Button
      id={buttonId}
      variant={isSubmit ? 'contained' : 'text'}
      type={isSubmit ? 'submit' : 'button'}
      onClick={isSubmit ? undefined : (onClick ?? onDismiss)}
      disabled={disabled || (!isSubmit && isSaving)}
      loading={loading}
      loadingPosition="start"
      loadingIndicator={
        <CircularProgress color="inherit" size={LOADER_SIZE} aria-labelledby={buttonId} />
      }
      startIcon={icon}
      disableElevation
      disableFocusRipple
      data-testid={testId}
      {...{ [MODAL_ACTION_ATTRIBUTE]: kind }}
      sx={getActionButtonSx(kind, hasDanger)}
    >
      {loading && loadingLabel ? loadingLabel : label}
    </Button>
  );
};

interface ModalContainerFooterProps {
  actions: ModalActions;
  onDismiss: () => void;
  isScrollable: boolean;
  isSaving: boolean;
}

export const ModalContainerFooter = ({
  actions,
  onDismiss,
  isScrollable,
  isSaving,
}: ModalContainerFooterProps) => {
  const hasDanger = actions.some(({ kind }) => kind === 'danger');

  return (
    <DialogActions sx={getModalFooterSx(isScrollable)}>
      {sortBy(actions, isSubmitAction).map((action) => (
        <ModalActionButton
          key={action.label}
          action={action}
          hasDanger={hasDanger}
          onDismiss={onDismiss}
          isSaving={isSaving}
        />
      ))}
    </DialogActions>
  );
};
